import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft, Search, RefreshCw } from 'lucide-react';

const NotFound = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [currentText, setCurrentText] = useState('Page not found...');

  const errorTexts = [
    'Page not found...',
    'Error 404 detected!'
  ];

  useEffect(() => {
    // Trigger entrance animation
    setIsVisible(true);

    // Change error text periodically
    const textInterval = setInterval(() => {
      setCurrentText(prev => {
        const currentIndex = errorTexts.indexOf(prev);
        const nextIndex = (currentIndex + 1) % errorTexts.length;
        return errorTexts[nextIndex];
      });
    }, 2000);

    return () => {
      clearInterval(textInterval);
    };
  }, []);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    window.history.length > 1 ? navigate(-1) : navigate('/');
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-gradient-to-br from-orange-100 via-orange-50 to-orange-200">
      {/* Animated Background Particles */}
      <div className="absolute inset-0">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-gradient-to-r from-red-500 to-orange-400 rounded-full opacity-40"
            initial={{
              x: typeof window !== 'undefined' ? Math.random() * window.innerWidth : Math.random() * 1200,
              y: typeof window !== 'undefined' ? Math.random() * window.innerHeight : Math.random() * 800,
              scale: 0
            }}
            animate={{
              x: typeof window !== 'undefined' ? Math.random() * window.innerWidth : Math.random() * 1200,
              y: typeof window !== 'undefined' ? Math.random() * window.innerHeight : Math.random() * 800,
              scale: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      {/* Geometric Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg width="100%" height="100%" className="absolute inset-0">
          <defs>
            <pattern id="error-grid" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M 80 0 L 0 0 0 80" fill="none" stroke="url(#error-gradient)" strokeWidth="1"/>
            </pattern>
            <linearGradient id="error-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ef4444" />
              <stop offset="50%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#eab308" />
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#error-grid)" />
        </svg>
      </div>

      {/* Main Content Container */}
      <div className="relative flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">

        {/* 404 Number with Glitch Effect */}
        <motion.div
          initial={{ scale: 0, rotate: -180, opacity: 0 }}
          animate={{ scale: isVisible ? 1 : 0, rotate: 0, opacity: 1 }}
          transition={{
            duration: 1.5,
            ease: "easeOut",
            type: "spring",
            stiffness: 80
          }}
          className="relative mb-6 sm:mb-8"
        >
          {/* Glitch Background */}
          <motion.div
            className="absolute inset-0 text-8xl sm:text-9xl md:text-[12rem] lg:text-[15rem] font-black text-red-500 opacity-20 blur-sm"
            animate={{
              x: [0, -2, 2, 0],
              y: [0, 1, -1, 0],
            }}
            transition={{
              duration: 0.2,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            404
          </motion.div>

          {/* Main 404 Text */}
          <motion.h1
            className="relative z-10 text-8xl sm:text-9xl md:text-[12rem] lg:text-[15rem] font-black bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 bg-clip-text text-transparent"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            404
          </motion.h1>

          {/* Floating Error Icons */}
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-r from-red-500 to-orange-400 rounded-full z-5"
              style={{
                left: `${50 + Math.cos((i * 90) * Math.PI / 180) * (window.innerWidth < 640 ? 80 : window.innerWidth < 1024 ? 120 : 160)}px`,
                top: `${50 + Math.sin((i * 90) * Math.PI / 180) * (window.innerWidth < 640 ? 80 : window.innerWidth < 1024 ? 120 : 160)}px`,
              }}
              animate={{
                scale: [1, 2, 1],
                opacity: [0.3, 1, 0.3],
                rotate: 360
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 0.5,
                ease: "easeInOut"
              }}
            />
          ))}
        </motion.div>

        {/* Logo Container */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="relative mb-6 sm:mb-8"
        >
          <motion.div
            className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 flex bg-black/[0.2] items-center justify-center"
            whileHover={{ scale: 1.5, rotate: 5 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <img
              src="https://cdn.acsdev.in/ManaPrint/manaprint_new.png"
              alt="ManaPrint Logo"
              className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 object-contain filter drop-shadow-xl opacity-80"
            />
          </motion.div>
        </motion.div>

        {/* Error Message */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="text-center mb-8 sm:mb-12 relative z-20"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
            Oops! Page Not Found
          </h2>

          {/* Dynamic Error Text */}
          <AnimatePresence mode="wait">
            {/* <motion.p
              key={currentText}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
              className="text-gray-600 text-base sm:text-lg md:text-xl mb-6 max-w-md mx-auto"
            >
              {currentText}
            </motion.p> */}
          </AnimatePresence>

          
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 sm:gap-6 relative z-20"
        >
          {/* Go Home Button */}
          <motion.button
            onClick={handleGoHome}
            className="flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 min-w-[160px]"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <Home className="w-5 h-5" />
            Go Home
          </motion.button>

          {/* Go Back Button */}
          <motion.button
            onClick={handleGoBack}
            className="flex items-center justify-center gap-3 px-6 py-3 bg-white text-gray-700 font-semibold rounded-full shadow-lg hover:shadow-xl border-2 border-gray-200 hover:border-orange-300 transition-all duration-300 min-w-[160px]"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-5 h-5" />
            Go Back
          </motion.button>

          {/* Refresh Button */}
          <motion.button
            onClick={handleRefresh}
            className="flex items-center justify-center gap-3 px-6 py-3 bg-white text-gray-700 font-semibold rounded-full shadow-lg hover:shadow-xl border-2 border-gray-200 hover:border-orange-300 transition-all duration-300 min-w-[160px]"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </motion.button>
        </motion.div>

        {/* Search Suggestion */}
        {/* <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="mt-8 sm:mt-12 text-center relative z-20"
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 bg-white bg-opacity-50 rounded-full text-gray-600 text-sm backdrop-blur-sm border border-white border-opacity-30"
            whileHover={{ scale: 1.05 }}
          >
            <Search className="w-4 h-4" />
            <span>Try searching for products or categories</span>
          </motion.div>
        </motion.div> */}

        {/* Animated Dots */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="flex space-x-2 mt-8 relative z-20"
        >
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="w-2 h-2 sm:w-3 sm:h-3 bg-gradient-to-r from-red-500 to-orange-400 rounded-full"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: i * 0.3,
                ease: "easeInOut"
              }}
            />
          ))}
        </motion.div>
      </div>

      {/* Corner Decorative Elements */}
      <motion.div
        className="absolute top-4 sm:top-6 lg:top-10 left-4 sm:left-6 lg:left-10 w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 border-l-2 sm:border-l-3 lg:border-l-4 border-t-2 sm:border-t-3 lg:border-t-4 border-red-500 opacity-30"
        animate={{ scale: [1, 1.1, 1], rotate: [0, 5, 0] }}
        transition={{ duration: 4, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-4 sm:bottom-6 lg:bottom-10 right-4 sm:right-6 lg:right-10 w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 border-r-2 sm:border-r-3 lg:border-r-4 border-b-2 sm:border-b-3 lg:border-b-4 border-orange-400 opacity-30"
        animate={{ scale: [1, 1.1, 1], rotate: [0, -5, 0] }}
        transition={{ duration: 4, repeat: Infinity, delay: 2 }}
      />

      {/* Broken Chain Animation (404 Theme) */}
      <motion.div
        className="absolute top-1/4 right-8 sm:right-16 lg:right-24"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.2 }}
        transition={{ delay: 1 }}
      >
        <motion.div
          className="w-8 h-8 sm:w-12 sm:h-12 border-4 border-red-400 rounded-full"
          animate={{
            rotate: [0, 10, -10, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.div
          className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-orange-400 rounded-full mt-2 ml-4"
          animate={{
            rotate: [0, -10, 10, 0],
            scale: [1, 0.9, 1]
          }}
          transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
        />
      </motion.div>

      {/* Mobile-specific floating elements */}
      <div className="sm:hidden">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={`mobile-error-${i}`}
            className="absolute w-1 h-1 bg-gradient-to-r from-red-500 to-orange-400 rounded-full opacity-30"
            style={{
              left: `${15 + i * 20}%`,
              top: `${15 + i * 15}%`,
            }}
            animate={{
              scale: [1, 2, 1],
              opacity: [0.3, 0.7, 0.3],
              y: [0, -10, 0]
            }}
            transition={{
              duration: 2 + i * 0.3,
              repeat: Infinity,
              delay: i * 0.4,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      <style jsx>{`
        /* Glitch effect for 404 text */
        @keyframes glitch {
          0% { transform: translate(0); }
          20% { transform: translate(-2px, 2px); }
          40% { transform: translate(-2px, -2px); }
          60% { transform: translate(2px, 2px); }
          80% { transform: translate(2px, -2px); }
          100% { transform: translate(0); }
        }

        .glitch {
          animation: glitch 0.3s infinite;
        }

        /* Responsive adjustments */
        @media (max-width: 640px) {
          .tracking-widest {
            letter-spacing: 0.1em;
          }
        }

        /* Reduced motion preferences */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .bg-gradient-to-r {
            background: #000 !important;
            color: #fff !important;
          }
        }
      `}</style>
    </div>
  );
};

export default NotFound;
